import { SiteElement } from '../types';
import { Trash2, Edit3 } from 'lucide-react';

interface ElementPropertiesProps {
  element: SiteElement;
  onUpdate: (updates: Partial<SiteElement>) => void;
  onDelete: () => void;
}

export function ElementProperties({ element, onUpdate, onDelete }: ElementPropertiesProps) {
  const getElementTypeLabel = (type: SiteElement['type']): string => {
    const labels: Record<SiteElement['type'], string> = {
      start: 'Départ',
      finish: 'Arrivée',
      'feed-zone': 'Ravitaillement',
      'wax-zone': 'Zone fartage',
      timing: 'Chronométrage',
      exchange: 'Zone échange',
      medical: 'Poste médical',
      spectator: 'Zone spectateurs',
      'loop-marker': 'Marqueur boucle',
      camera: 'Caméra',
      'weigh-zone': 'Pesée',
      parking: 'Parking',
      'locker-room': 'Vestiaires',
      'warmup-zone': 'Échauffement',
      tribune: 'Tribune',
      wc: 'WC',
      'ski-drop': 'Dépose de ski',
      'danger-zone': 'Danger/Exclusion',
      custom: 'Personnalisé',
      text: 'Texte'
    };
    return labels[type] || 'Inconnu';
  };

  return (
    <div className="space-y-3">
      {/* Ligne 1 : Informations de base */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        <div>
          <label className="block text-xs text-gray-700 mb-1">Type</label>
          <input
            type="text"
            value={getElementTypeLabel(element.type)}
            disabled
            className="w-full px-2 py-1.5 text-sm bg-gray-50 border border-gray-300 rounded"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-700 mb-1">Libellé</label>
          <input
            type="text"
            value={element.label}
            onChange={(e) => onUpdate({ label: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            placeholder="Nom de l'élément"
          />
        </div>

        {(element.type === 'start' || element.type === 'finish') && (
          <div>
            <label className="block text-xs text-gray-700 mb-1">Altitude (m)</label>
            <input
              type="number"
              value={element.altitude || ''}
              onChange={(e) => onUpdate({ altitude: parseInt(e.target.value) || undefined })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              placeholder="ex: 2500"
            />
          </div>
        )}

        {(element.type === 'timing' || element.type === 'loop-marker' || element.type === 'feed-zone') && (
          <div>
            <label className="block text-xs text-gray-700 mb-1">Distance (m)</label>
            <input
              type="number"
              value={element.distance || ''}
              onChange={(e) => onUpdate({ distance: parseInt(e.target.value) || undefined })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              placeholder="ex: 5000"
            />
          </div>
        )}

        {element.type !== 'text' && (
          <div>
            <label className="block text-xs text-gray-700 mb-1">Taille: {element.size || 48}px</label>
            <input
              type="range"
              min="24"
              max="96"
              step="4"
              value={element.size || 48}
              onChange={(e) => onUpdate({ size: parseInt(e.target.value) })}
              className="w-full mt-1"
            />
          </div>
        )}

        {element.type === 'text' && (
          <div>
            <label className="block text-xs text-gray-700 mb-1">Police: {element.fontSize || 16}px</label>
            <input
              type="range"
              min="10"
              max="72"
              step="2"
              value={element.fontSize || 16}
              onChange={(e) => onUpdate({ fontSize: parseInt(e.target.value) })}
              className="w-full mt-1"
            />
          </div>
        )}

        {(element.type === 'custom' || element.type === 'text') && (
          <div>
            <label className="block text-xs text-gray-700 mb-1">Couleur</label>
            <input
              type="color"
              value={element.color || '#8b5cf6'}
              onChange={(e) => onUpdate({ color: e.target.value })}
              className="w-full h-[30px] border border-gray-300 rounded cursor-pointer"
            />
          </div>
        )}
      </div>

      {/* Ligne 2 : Notes */}
      <div>
        <label className="block text-xs text-gray-700 mb-1">Notes</label>
        <textarea
          value={element.notes || ''}
          onChange={(e) => onUpdate({ notes: e.target.value })}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
          rows={2}
          placeholder="Informations complémentaires..."
        />
      </div>

      {/* Actions */}
      <div className="border-t pt-2 flex items-center justify-between">
        <span className="text-xs text-gray-500">Position: X: {Math.round(element.position.x)}, Y: {Math.round(element.position.y)}</span>
        <button
          type="button"
          onClick={onDelete}
          className="text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Supprimer
        </button>
      </div>
    </div>
  );
}